'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useLocale } from 'next-intl';
import { useEffect } from 'react';

import { TRACKED_EVENT_NAME } from '@/constants/gtag';
import { waitForGtag } from '@/utils/gtag';

import { SECTION_NAME_ATTR, SECTION_VIEW_ATTR } from './section-view-sentinel';

/**
 * 메인페이지 섹션 도달 추적. DOM 의 SectionViewSentinel 을 IntersectionObserver
 * 하나로 관찰해, 기준선이 뷰포트에 들어오면 section_view 를 Datadog RUM 과 GA4 에
 * 보낸다. 파라미터는 싱크별 관례를 따른다 — Datadog 은 { section, section_name, page },
 * GA4 는 다른 이벤트와 같은 { event_label, user_language } 에 섹션 순서를 예약 파라미터
 * value 로 실어 커스텀 측정기준을 새로 만들지 않는다(페이지는 page_location 으로 구분).
 * ⚠️ value 가 전환 가치로 합산되므로 section_view 를 GA4 키 이벤트로 지정하지 말 것.
 *
 * 페이지뷰당 섹션별 1회 — 첫 발화 뒤 unobserve. 세션 단위는 Datadog 에서
 * session.id distinct 로 집계한다.
 */
export default function SectionViewTracker() {
  const locale = useLocale();

  useEffect(() => {
    const sentinels = document.querySelectorAll(`[${SECTION_VIEW_ATTR}]`);
    if (sentinels.length === 0) return;

    const page = `main_${locale}`;
    const gtagCleanups: (() => void)[] = [];

    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        observer.unobserve(entry.target);

        const section = Number(entry.target.getAttribute(SECTION_VIEW_ATTR));
        const sectionName = entry.target.getAttribute(SECTION_NAME_ATTR);

        datadogRum.addAction(TRACKED_EVENT_NAME.SECTION_VIEW, {
          section,
          section_name: sectionName,
          page,
        });
        gtagCleanups.push(
          waitForGtag(() => {
            window.gtag('event', TRACKED_EVENT_NAME.SECTION_VIEW, {
              event_label: sectionName,
              user_language: locale,
              value: section,
            });
          }),
        );
      }
    });

    sentinels.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
      gtagCleanups.forEach(cleanup => cleanup());
    };
  }, [locale]);

  return null;
}
